/**
 * routes.tsx — the portal's route TABLE, as a config both `App.tsx` (the
 * classic `<Routes>` tree every existing spec mounts under a plain
 * `<MemoryRouter>`) and `main.tsx` (production's `createBrowserRouter`, C1.8
 * router migration) render from — one list, two mounting strategies, so the
 * route→component mapping can never drift between them.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: patient-journey
 * navigation resilience (feat/patient-journey-navigation-resilience).
 *
 *   /                     redirects to /weightloss (Core-hosted: to the signed-in
 *                         person's own portal, or back to Core)
 *   /auth                 sign in / register / forgot / reset / verify
 *   /auth-pharmacy        pharmacy sign-in (emailed link token + personal code)
 *   /auth-core            GPS Core → Clinics sign-in handoff (Core sign-in token in #;
 *                         → /auth off the Core-hosted image)
 *   /patients             PATIENT-gated dashboard
 *   /patients/profile     PATIENT-gated profile
 *   /clinicians           CLINICIAN-gated console (+ the retired forms/new redirect)
 *   /admin                ADMIN-gated console
 *   /pharmacy             PHARMACY-gated dispensing console
 *   /resubscribe          PUBLIC end-of-term confirmation (capability token in #)
 *   /preferences          PUBLIC marketing-consent choices from an email link (token in #)
 *   /unlock               PUBLIC engagement unlock page (capability token in #)
 *   /thank-you            the END of checkout — confirmation at a URL of its own
 *   /weightloss-clinical-safety   PUBLIC clinical-safety reference page (noindex)
 *   /weightloss-clinical-safty    the CTO's original spelling → permanent redirect
 *   /:clinicSlug/callback the free callback lane
 *   /:clinicSlug          public clinic journey → plan → checkout (R28 public copy)
 *
 * react-router v6 scores static segments above dynamic ones, so /auth,
 * /auth-pharmacy, /auth-core, /patients, /clinicians, /admin, /pharmacy, /resubscribe,
 * /preferences, /thank-you and the two clinical-safety paths always win over /:clinicSlug. The role gates are presentation only — the
 * gateway enforces everything (R41).
 *
 * WHY `RouteObject[]` (not JSX `<Route>` elements): `createBrowserRouter` /
 * `createMemoryRouter` take this shape directly; `App.tsx` maps it back into
 * `<Route path element>` for the classic tree. Every `element` here is a
 * LEAF (the AppShell/DesktopOnly/RequireRole layout wrapping lives in the
 * element itself, not as nested route children) — this list intentionally
 * stays flat so both consumers can render it identically.
 */
import { Navigate, type RouteObject } from 'react-router-dom';
import { AuthPage } from './auth/AuthPage';
import { CoreHandoffPage } from './auth/CoreHandoffPage';
import { CoreHostGuard, IndexRedirect } from './auth/CoreHostGuard';
import { RequireRole } from './auth/RequireRole';
import { DesktopOnly } from './auth/DesktopOnly';
import { CallbackPage } from './journey/CallbackPage';
import { NotReadyPage } from './journey/NotReadyPage';
import { JourneyPage } from './journey/JourneyPage';
import { PatientPortal } from './patient-portal/PatientPortal';
import { CliniciansPage } from './clinicians/CliniciansPage';
import { AdminPage } from './admin/AdminPage';
import { PharmacyAuthPage } from './pharmacy/PharmacyAuthPage';
import { PharmacyPage } from './pharmacy/PharmacyPage';
import { RequirePharmacySession } from './pharmacy/RequirePharmacySession';
import { ResubscribePage } from './resubscribe/ResubscribePage';
import { PreferencesPage } from './marketing/PreferencesPage';
import { PREFERENCES_PATH } from './marketing/route';
import { UnlockPage } from './unlock/UnlockPage';
import { UNLOCK_PATH } from './unlock/route';
import { ThankYouPage } from './checkout/ThankYouPage';
import { THANK_YOU_PATH } from './checkout/thankYouRoute';
import { ClinicalSafetyPage } from './safety/ClinicalSafetyPage';
import { CLINICAL_SAFETY_LEGACY_PATH, CLINICAL_SAFETY_PATH } from './safety/route';

export const routeTable: RouteObject[] = [
  // Standalone: → /weightloss as always. Core-hosted: → the signed-in person's own
  // portal, or back to Core — never out to the standalone marketing front door.
  { path: '/', element: <IndexRedirect /> },
  { path: '/auth', element: <AuthPage /> },
  // The pharmacy lane's own front door. NOT a `?mode=` on /auth: that page is
  // built around email + password, and a pharmacist has neither — they arrive
  // on a single-use emailed link and prove themselves with a personal code.
  { path: '/auth-pharmacy', element: <PharmacyAuthPage /> },
  // The GPS Core → Clinics sign-in handoff (Clinics-in-Core, Clinics ADR-1, 2026-09-07).
  // Present in EVERY build so the route table stays one list; off the Core-hosted
  // image it simply sends the visitor to /auth. The Core sign-in token rides in
  // the URL FRAGMENT, exactly like /auth-pharmacy and /resubscribe. It is also in
  // the tracking gate's RESERVED_SEGMENTS (tracking/gtm.ts) — a single-segment
  // path that must never load GTM.
  { path: '/auth-core', element: <CoreHandoffPage /> },
  // UX Wave 10: /patients is the portal SHELL (rail + sections); the default
  // section is the Dashboard overview and /patients/profile is the Profile
  // section — so every existing patient deep link lands unchanged.
  {
    path: '/patients',
    element: (
      <RequireRole role="PATIENT">
        <PatientPortal />
      </RequireRole>
    ),
  },
  {
    path: '/patients/profile',
    element: (
      <RequireRole role="PATIENT">
        <PatientPortal />
      </RequireRole>
    ),
  },
  {
    path: '/clinicians',
    element: (
      <DesktopOnly>
        <RequireRole role="CLINICIAN">
          <CliniciansPage />
        </RequireRole>
      </DesktopOnly>
    ),
  },
  // The old fill-on-behalf page (P1E) retired 2026-07-21 — the CRM pipeline's
  // Create New + in-drawer questionnaire replaced it. Old bookmarks land on
  // the pipeline.
  { path: '/clinicians/forms/new', element: <Navigate to="/clinicians" replace /> },
  {
    path: '/admin',
    element: (
      <DesktopOnly>
        <RequireRole role="ADMIN">
          <AdminPage />
        </RequireRole>
      </DesktopOnly>
    ),
  },
  // The dispensing console. DesktopOnly like the other two consoles: reading a
  // dose and a delivery address off a phone-width screen is a dispensing-safety
  // risk, and a dispensary works at a workstation. The gate is
  // RequirePharmacySession, not RequireRole — a pharmacy session lives in its
  // own store (see pharmacy/session.ts).
  {
    path: '/pharmacy',
    element: (
      <DesktopOnly>
        <RequirePharmacySession>
          <PharmacyPage />
        </RequirePharmacySession>
      </DesktopOnly>
    ),
  },
  // The end-of-term re-subscribe confirmation. UNGATED by design: the patient
  // arrives from an emailed link and must not be bounced through a sign-in they
  // may not remember. The capability is the token in the URL FRAGMENT (never a
  // query string — see ResubscribePage), and what it can do is tightly bounded:
  // it places a hold on the card already on file for a plan that patient already
  // bought, and a clinician still has to approve before any money moves.
  { path: '/resubscribe', element: <ResubscribePage /> },
  // Marketing-consent choices (2026-09-11) — the page linked from the bottom of
  // every marketing email as `/preferences#t=<token>`. UNGATED for the same
  // reason as /resubscribe: an unsubscribe must work for someone who has never
  // made an account, months later. The capability is the signed token in the
  // FRAGMENT, and all it can do is change this one person's two consent
  // booleans. In the tracking gate's RESERVED_SEGMENTS — no GTM on it, ever.
  { path: PREFERENCES_PATH, element: <PreferencesPage /> },
  // The patient unlock page (2026-09-23) — `/unlock#t=<token>`, the link
  // engagement emails and WhatsApp copies carry. UNGATED for the same reason
  // as /resubscribe and /preferences: it lands in an inbox or a WhatsApp
  // thread and must work months later on a phone with no session. The
  // capability is the token in the FRAGMENT (read + scrubbed, body-only on
  // the wire), and what it can do is bounded server-side: read what one
  // deal's link offers, and download content the server derives as unlocked.
  // In the tracking gate's RESERVED_SEGMENTS — no GTM on it, ever.
  { path: UNLOCK_PATH, element: <UnlockPage /> },
  // The public clinical-safety reference page. UNGATED on purpose, and unusually
  // so for a surface that names prescription-only medicines: it has to work from
  // a link in a dispatch email, from a clinician's bookmark and from a phone
  // that has never held a session here. What makes that safe is that the page
  // carries information and no offer — read the header of ClinicalSafetyPage
  // before adding anything to it. It is `noindex` for the same reason.
  { path: CLINICAL_SAFETY_PATH, element: <ClinicalSafetyPage /> },
  // The CTO's ORIGINAL SPELLING, kept alive permanently. That URL may already
  // have been shared before the spelling was corrected, and a safety link that
  // 404s is the worst dead link this portal can serve — the person following it
  // is the person most likely to need what is behind it.
  //
  // `replace` so the misspelling never enters browser history: without it, Back
  // from the real page lands on the redirect, which immediately forwards again
  // and traps the reader on a page they are trying to leave.
  //
  // A CLIENT-SIDE redirect, not a 301 at the edge. nginx serves index.html for
  // every path (SPA fallback) and the router owns the route table, so putting
  // the rule anywhere else would split one decision across two files. Nothing is
  // lost by it: the page is `noindex`, so there is no crawler whose view of the
  // canonical URL we are trying to correct — only readers, and they land on the
  // right page either way.
  { path: CLINICAL_SAFETY_LEGACY_PATH, element: <Navigate to={CLINICAL_SAFETY_PATH} replace /> },
  // THE END OF CHECKOUT, at a URL of its own (2026-08-20). Marketing asked for
  // a separate thank-you page "to track conversions with URL change" — until now
  // the confirmation was a component swap inside /:clinicSlug and the address bar
  // never moved, so a GTM History Change trigger had nothing to fire on.
  //
  // UNGATED, and it has to be: a guest who paid has no account yet (they create
  // one during checkout), so any role guard here would lock the paying patient
  // out of their own confirmation. Nothing is exposed by that — the page reads no
  // order and holds no identifier; what it shows arrives in the history entry
  // from checkout itself (see checkout/thankYouRoute.ts), and with nothing in
  // hand it says so rather than inventing a confirmation.
  //
  // A SINGLE static segment ON PURPOSE: tracking/gtm.ts's `isTrackedPath` only
  // tracks one-segment paths and /:clinicSlug/callback, so a nested
  // /weightloss/thank-you would have shipped marketing a page with no container
  // on it. See checkout/thankYouRoute.ts for the whole argument.
  { path: THANK_YOU_PATH, element: <ThankYouPage /> },
  // The public acquisition journey and the free callback lane. Wrapped in
  // CoreHostGuard (Clinics-in-Core, 2026-09-07): on the image GPS Core serves
  // under /clinics/* they hand the visitor to the standalone portal, where the
  // front door for people who are not yet patients has always lived. On every
  // other build the guard is a pass-through and these render exactly as before.
  // T-2 (2026-09-11) — "what's holding you back?". A TWO-segment path ON
  // PURPOSE: `isTrackedPath` admits only one-segment paths and
  // /:clinicSlug/callback, so GTM never loads on a page listing health-adjacent
  // reasons. gtm.spec pins that. Same CoreHostGuard as its sibling.
  {
    path: '/:clinicSlug/not-ready',
    element: (
      <CoreHostGuard>
        <NotReadyPage />
      </CoreHostGuard>
    ),
  },
  {
    path: '/:clinicSlug/callback',
    element: (
      <CoreHostGuard>
        <CallbackPage />
      </CoreHostGuard>
    ),
  },
  {
    path: '/:clinicSlug',
    element: (
      <CoreHostGuard>
        <JourneyPage />
      </CoreHostGuard>
    ),
  },
];
